// Business-facing entry point for adding an account. The partner generates a link
// (POST /v2/accounts/link, Melio-Entity-Id header) and hands the resulting URL to the
// business; opening it lands here. The scoped token in the URL carries the entity id and,
// for Plaid, the embedded Plaid link_token — so the flow runs with no partner api-key in the
// browser and submits via POST /v2/accounts (Bearer <token>, Melio-Entity-Id header).
//
// Full end-to-end reference (this portal flow + the partnerships-api backend, card + plaid):
// the `/create-funding-source-link` skill in partnerships-api (.claude/commands/).
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { config } from '@/config';
import { defaultEnvironment } from '@/config/environments';
import { decodeInternalAccountLinkToken, isTokenExpired } from '@/lib/sessionToken';
import { ensureSonarSession } from '@/lib/sonarSdk';

import { AddCardAccount } from '@/containers/AddCardAccount';
import { AddPlaidAccount } from '@/containers/AddPlaidAccount';
import { AccountShell, AccountError } from '@/containers/AccountShell';

export default function AddAccountRoute() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const typeParam = params.get('type');

  const env = useMemo(() => defaultEnvironment(config.environment, window.location.hostname), []);
  const claims = useMemo(() => (token ? decodeInternalAccountLinkToken(token) : null), [token]);

  useEffect(() => {
    if (!claims) return;
    void ensureSonarSession(env, claims.partnerName, claims.entityId);
  }, [env, claims]);

  if (!token || !claims) {
    return (
      <AccountShell>
        <AccountError title="This link isn’t valid" message="The link is missing or malformed. Ask your provider to send you a new one." />
      </AccountShell>
    );
  }

  if (isTokenExpired(claims)) {
    return (
      <AccountShell>
        <AccountError
          title="This link has expired"
          message="For your security the link is only valid for a short time. Ask your provider to send you a new one."
        />
      </AccountShell>
    );
  }

  const type = typeParam ?? claims.fundingSourceType;

  // No AccountShell here — Plaid's own overlay is the only UI most of the time, and the
  // sized/padded shell box would otherwise sit empty in the DOM behind it.
  if (type === 'plaid') {
    return <AddPlaidAccount env={env} token={token} claims={claims} />;
  }

  if (type === 'card') {
    return (
      <AccountShell>
        <AddCardAccount env={env} token={token} claims={claims} />
      </AccountShell>
    );
  }

  return (
    <AccountShell>
      <AccountError title="This link isn’t valid" message="Unknown funding source type. Ask your provider to send you a new one." />
    </AccountShell>
  );
}
